import { useState, useEffect, useMemo, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, ShieldCheck, Phone } from "lucide-react";
import { api } from "../lib/api";
import { getSessionId, setUserId } from "../lib/utm";
import { initMetaPixel, trackLead } from "../lib/pixel";
import { getInviteSlug, getInviteTag } from "../lib/invite";
import { toast } from "sonner";
import BrandHeader, { normalizeColor, safeButtonColor } from "./BrandHeader";

/** Banking products require a mobile number BEFORE we generate the affiliate
 *  URL. The number becomes the customer's identity across future clicks, and
 *  their HITCH-USER-XXXXXX slug flows as `p2` to the network.
 *
 *  Props:
 *   - `product` (obj)   → source of truth for brand header. When passed we
 *     render the partner banner on top: uses `product.capture_image_url` if
 *     set, else a branded tile with the partner's favicon + `brand_color`.
 *     Also used to render "Provider · title/subtitle" details. Backward
 *     compatible — legacy callers passing only `brand` still work.
 *   - `optional` (bool)  → when true, adds a "Skip for now" secondary action.
 *   - `ctaLabel` (string) → override the primary button text.
 *   - `title` / `description` (string) → override header copy.
 *   - `metaPixelId` (string) → if set, fires Meta Pixel PageView when the
 *     dialog opens + Lead event when the phone is submitted, scoped to
 *     that specific pixel id. Enables per-lender ad attribution.
 */

const DEFAULT_BRAND_COLOR = "#0055FF";

export default function MobileCaptureDialog({
  open,
  onOpenChange,
  onCaptured,
  onSkip,
  product,          // optional — enables the brand header. See docstring.
  brand: brandProp, // legacy — used only when `product` is not passed
  optional = false,
  ctaLabel,
  title,
  description,
  badge,            // override the "Required for banking products" badge text
  metaPixelId: metaPixelIdProp,
  inviteSlug: inviteSlugProp,  // admin invite context (see /pages/InviteLanding)
  inviteTag: inviteTagProp,    // master-invite variable (mobile / ref id)
  prefilledPhone,              // preseeded number when the invite carries one
}) {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  // Prefer product-derived values; fall back to legacy props.
  const brand = useMemo(
    () => product?.provider || product?.title || brandProp || "the lender",
    [product, brandProp]
  );
  const metaPixelId = metaPixelIdProp || product?.meta_pixel_id;
  const primaryColor = normalizeColor(product?.brand_color);
  const buttonColor = safeButtonColor(product?.brand_color);
  // Invite slug source of truth: explicit prop > URL/sessionStorage helper.
  // We resolve it lazily (per keystroke / submit) so slug updates propagate
  // without needing to re-mount the dialog.
  const resolveInviteSlug = () => inviteSlugProp || getInviteSlug();
  const resolveInviteTag = () => inviteTagProp || getInviteTag();

  // Prefill the phone field the moment the dialog opens for a fresh product.
  // Keep the value editable — customer might correct a typo the admin made.
  useEffect(() => {
    if (open && prefilledPhone) {
      // Show with country prefix so the field looks pre-verified.
      setPhone("+91 " + String(prefilledPhone).replace(/\D/g, "").slice(-10));
    } else if (!open) {
      setPhone("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefilledPhone, product?.id]);

  // ---- Debounced keystroke beacon (invite tracking) ----
  // Fires 800ms after the last keystroke so admin can see partial numbers
  // even for abandoned invites (customer types 6 digits, closes tab). Also
  // flushes on unmount / dialog close via a ref-guarded final send so nothing
  // is lost if the user closes the tab before the debounce fires.
  const typedTimerRef = useRef(null);
  const lastTypedValueRef = useRef("");
  const flushTyped = (value) => {
    const slug = resolveInviteSlug();
    if (!slug || !value) return;
    // Fire and forget — never block on tracking.
    // Use fetch with keepalive so a pending POST survives a tab close.
    try {
      const url = `${api.defaults.baseURL}/invites/${slug}/typed`;
      const tag = resolveInviteTag();
      fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value, ...(tag ? { tag } : {}) }),
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* silent */ }
  };
  useEffect(() => {
    if (!open) return;
    // Reset when dialog opens fresh; existing prefill counts as typed too.
    return () => {
      if (typedTimerRef.current) {
        clearTimeout(typedTimerRef.current);
        typedTimerRef.current = null;
      }
      // Final flush on unmount / close.
      if (lastTypedValueRef.current) flushTyped(lastTypedValueRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handlePhoneChange = (e) => {
    const v = e.target.value;
    setPhone(v);
    lastTypedValueRef.current = v;
    if (!resolveInviteSlug()) return;  // No invite → no beacon.
    if (typedTimerRef.current) clearTimeout(typedTimerRef.current);
    typedTimerRef.current = setTimeout(() => flushTyped(v), 800);
  };

  // Fire the lender-specific Meta Pixel PageView every time this dialog
  // opens for a product with a configured pixel id.
  useEffect(() => {
    if (open && metaPixelId) initMetaPixel(metaPixelId);
  }, [open, metaPixelId]);

  const submit = async (e) => {
    e.preventDefault();
    // Normalise: strip all non-digits, then strip common India prefixes so we
    // always store a clean 10-digit number.
    //   +91 98765 43210 -> 919876543210 -> 9876543210
    //   0 98765 43210   -> 09876543210  -> 9876543210
    let digits = phone.replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
    else if (digits.length === 13 && digits.startsWith("091")) digits = digits.slice(3);
    else if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
    if (digits.length !== 10 || !/^[6-9]/.test(digits)) {
      toast.error("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setBusy(true);
    try {
      const inviteSlug = resolveInviteSlug();
      const inviteTag = inviteSlug ? resolveInviteTag() : null;
      const { data } = await api.post("/customers/identify", {
        phone: digits,
        session_id: getSessionId(),
        ...(inviteSlug ? { invite_slug: inviteSlug } : {}),
        ...(inviteTag ? { invite_tag: inviteTag } : {}),
      });
      if (data?.hitch_user_id) setUserId(data.hitch_user_id);
      // Fire per-lender Meta Pixel Lead event so the ad account tied to
      // this pixel sees the conversion (phone captured for this lender).
      if (metaPixelId) trackLead(metaPixelId, { content_name: brand });
      toast.success("Verified — redirecting to " + brand);
      onCaptured?.(data);
      onOpenChange(false);
    } catch (err) {
      // Show a specific message when we can. Network / server errors have
      // `err.response`; anything else (JS exception thrown post-await —
      // e.g. a rare localStorage failure) shows the raw error message so
      // the user isn't misled into thinking their phone wasn't saved.
      const serverDetail = err?.response?.data?.detail;
      if (serverDetail) {
        toast.error(serverDetail);
      } else if (err?.message && !err.response) {
        toast.error(`Something went wrong: ${err.message}. Please retry.`);
      } else {
        toast.error("Could not save. Try again.");
      }
      // Log the actual error to the console so we can debug via user's
      // devtools if this happens again.
      // eslint-disable-next-line no-console
      console.error("[MobileCaptureDialog] identify/callback error:", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="mobile-capture-dialog">
        <DialogHeader>
          {/* Partner brand header — only rendered when caller passes `product`. */}
          <BrandHeader product={product} />

          <div
            className="inline-flex items-center gap-2 badge-mono mb-1"
            style={{ color: primaryColor }}
          >
            <ShieldCheck className="w-3.5 h-3.5" />{" "}
            {badge || (optional ? "Optional — earn referral credit" : "Required for banking products")}
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {title || "Enter your mobile to continue"}
          </DialogTitle>
          <DialogDescription className="text-sm text-neutral-600">
            {description || `${brand} needs to verify you're a real applicant. We share only your mobile — never spam you.`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="mc-phone" className="text-xs uppercase tracking-[0.15em] text-neutral-500">
              Mobile number *
            </Label>
            <div className="relative mt-1">
              <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input
                id="mc-phone"
                data-testid="mc-phone"
                inputMode="tel"
                autoComplete="tel"
                maxLength={14}
                placeholder="+91 98765 43210"
                value={phone}
                onChange={handlePhoneChange}
                className="pl-9"
                autoFocus
                required
              />
            </div>
            {prefilledPhone && (
              <p
                className="text-[11px] mt-1.5 flex items-center gap-1"
                style={{ color: primaryColor }}
                data-testid="mc-prefilled-note"
              >
                <ShieldCheck className="w-3 h-3" />
                Prefilled from your invite — you can edit
              </p>
            )}
          </div>

          <Button
            type="submit"
            data-testid="mc-submit"
            disabled={busy}
            className="w-full h-11 text-white transition-colors"
            style={{
              backgroundColor: busy ? "#000" : buttonColor,
            }}
            onMouseEnter={(e) => { if (!busy) e.currentTarget.style.opacity = "0.9"; }}
            onMouseLeave={(e) => { if (!busy) e.currentTarget.style.opacity = "1"; }}
          >
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {ctaLabel || `Continue to ${brand}`}
          </Button>
          {optional && (
            <button
              type="button"
              data-testid="mc-skip"
              onClick={() => { onSkip?.(); onOpenChange(false); }}
              className="w-full text-xs text-neutral-500 hover:text-neutral-900 underline underline-offset-4"
            >
              Skip for now — share as anonymous
            </button>
          )}
          <p className="text-[11px] text-neutral-500 text-center leading-relaxed">
            By continuing you agree to be contacted about this application. See our{" "}
            <a href="/legal/privacy" className="underline">Privacy Policy</a>.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
